import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import MinimalHome from './pages/MinimalHome';
import MinimalDirectory from './pages/MinimalDirectory';
import MinimalWeather from './pages/MinimalWeather';
import MinimalToday from './pages/MinimalToday';
import MinimalStrava from './pages/MinimalStrava';
import MinimalCLI from './pages/MinimalCLI';
import MinimalProjects from './pages/MinimalProjects';
import MinimalPublications from './pages/MinimalPublications';
import MinimalWeight from './pages/MinimalWeight';
import MinimalParkQR from './pages/MinimalParkQR';
import NotFound from './components/pages/NotFound';
import { Clock } from './components/minimal/Clock';

const OAuthHandler = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  useEffect(() => {
    const code = searchParams.get('code');
    const error = searchParams.get('error');
    const state = searchParams.get('state');

    if (code || error) {
      const redirectPath = state ? decodeURIComponent(state) : '/strava';
      const params = new URLSearchParams();
      if (code) params.set('code', code);
      if (error) params.set('error', error);
      navigate(`${redirectPath}?${params.toString()}`, { replace: true });
    }
  }, [searchParams, navigate]);

  return null;
};

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  const isHome = pathname === '/';

  return (
    <div className="min-h-dvh bg-bg text-fg font-sans">
      <div className="mx-auto max-w-xl px-6 py-12 md:py-20">
        <header className="stagger-item mb-16 flex items-baseline justify-between gap-4">
          {isHome ? (
            <span className="text-xs text-faint" aria-hidden="true">
              &nbsp;
            </span>
          ) : (
            <Link to="/" className="quiet-link text-xs text-muted hover:text-fg">
              Abhinav Chinnusamy
            </Link>
          )}
          <Clock />
        </header>
        <main>{children}</main>
        <OAuthHandler />
      </div>
    </div>
  );
};

const App = () => {
  return (
    <Router>
      <Helmet>
        <title>Abhinav Chinnusamy</title>
        <meta name="description" content="Hardware engineer at Annapurna Labs. MS ECE, UW-Madison." />
        <meta name="theme-color" content="#ffffff" />
        <link rel="icon" type="image/svg+xml" href="/icons/favicon-a.svg" />
        <link rel="icon" type="image/png" sizes="192x192" href="/icons/favicon-a-192.png" />
      </Helmet>
      <Layout>
        <Routes>
          <Route path="/" element={<MinimalHome />} />
          <Route path="/ls" element={<MinimalDirectory />} />
          <Route path="/today" element={<MinimalToday />} />
          <Route path="/strava" element={<MinimalStrava />} />
          <Route path="/cli" element={<MinimalCLI />} />
          <Route path="/projects" element={<MinimalProjects />} />
          <Route path="/publications" element={<MinimalPublications />} />
          <Route path="/weather" element={<MinimalWeather />} />
          <Route path="/weight" element={<MinimalWeight />} />
          <Route path="/parkqr" element={<MinimalParkQR />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Layout>
    </Router>
  );
};

export default App;
